import { formattedDate, formattedDateTimezone } from 'helpers/utils';
import { b64toBlob } from 'helpers/fileServices';
import {
  EMethodDB,
  EStatusChangeKind,
  TAnimalApplicantDetail,
  TAnimalApplicantDetailUi,
  TAnimalResidenciesDetailsDB,
  TAnimalResidenciesDetailsUI,
  TApplicantAnimalScreeningDB,
  TApplicantAnimalScreeningUI,
  TApplicationDetailDB,
  TApplicationDetailsDB,
  TApplicationDetailsUI,
  TApplicationDetailUI,
  TAppMethodDetailsDB,
  TAppMethodDetailsUI,
  TApproveApplicationDB,
  TApproveApplicationFormProps,
  TApproveApplicationFormPropsUI,
  TApproveApplicationUI,
  TApprovePassedMoveInApplicationDB,
  TApprovePassedMoveInApplicationUI,
  TStatusChangeHistoryDB,
  TStatusChangeHistoryUI,
  TTenantResidenciesDB,
  TTenantResidenciesDetailsDB,
  TTenantResidenciesDetailsUI,
  TVerificationMessageDB,
  TVerificationMessageUi,
} from '../types/application.types';
import { TCreateConverter, TFetchConverter } from '../types/converter.types';
import {
  animalBasicInfoConvertor,
  animalOwnerConvertor,
  animalPaymentStatusConverter,
  animalTagsConverter,
  animalVetRecordConvertor,
  applicationSADocPacketConvertor,
  dataOverviewConvertor,
} from './animals.converter';
import {
  applicationPolicyConvertor,
  applicationPropertyConvertor,
  applicationResidentConvertor,
  applicationUnitConvertor,
  methodsConvertor,
} from './applicationGeneral.converters';
import { getHPDocPacketsConvertor } from './docPacket.convertors';
import { statusChangeInfoConverter } from './general.converters';
import {
  animalApplicationStatusConverter,
  animalResidencyStatusConverter,
  animalResidencyTagConverter,
  animalRestrictionStatusConvertor,
  animalStatusDetailsConverter,
  animalRestrictionTagConvertor,
} from './residence.converters';
import { hudGuidelinesLink } from '../datas/genericData';
import { EPolicyOverviewStatusDB } from '../types/animals.types';

export const applicationMethodDetailsConvertor: TFetchConverter<TAppMethodDetailsUI, TAppMethodDetailsDB> = {
  fromDb: (data) => ({
    leaseToDate: data.leaseToDate || null,
    moveInDate: data.moveInDate || null,
    property: applicationPropertyConvertor.fromDb({
      propertyId: data.propertyId,
      propertyName: data.propertyName,
    }),
    policy: applicationPolicyConvertor.fromDb({
      policyId: data.policyId,
      policyName: data.policyName,
    }),
    unit: applicationUnitConvertor.fromDb({
      unitId: data.unitId,
      unitNumber: data.unitNumber,
    }),
  }),
};

export const applicationAnimalResidenciesConverter: TFetchConverter<
  TAnimalResidenciesDetailsUI,
  TAnimalResidenciesDetailsDB
> = {
  fromDb: (data) => {
    return {
      id: data.id,
      animalName: data.animalName,
      animalProfilePicture: data.animalProfilePicture,
      animalType: data.animalType,
      animalWeight: data.animalWeight,
      animalSpecie: data.animalSpecie,
    };
  },
};

export const applicationResidenciesConverter: TFetchConverter<
  TTenantResidenciesDetailsUI,
  TTenantResidenciesDetailsDB
> = {
  fromDb: (data) => {
    return {
      id: data.id,
      unitNumber: data.unitNumber,
      propertyName: data.propertyName,
      animalResidencies: data.animalResidencies.map((item) => applicationAnimalResidenciesConverter.fromDb(item)),
    };
  },
};

export const applicationDetailConverter: TFetchConverter<TApplicationDetailUI, TApplicationDetailDB> = {
  fromDb: (data) => {
    const residencyData = data as TTenantResidenciesDB;
    const appMethodDetailData = data as TAppMethodDetailsDB;
    return {
      ...((residencyData.residencies || [])?.length && {
        residencies: residencyData.residencies.map((item) => applicationResidenciesConverter.fromDb(item)),
      }),
      ...(!(residencyData.residencies || [])?.length && applicationMethodDetailsConvertor.fromDb(appMethodDetailData)),
    };
  },
};

export const applicationDetailsConvertor: TFetchConverter<TApplicationDetailsUI, TApplicationDetailsDB> = {
  fromDb: (data) => ({
    resident: applicationResidentConvertor.fromDb({
      residentPhone: data.residentPhone,
      residentName: data.residentName,
      residentEmail: data.residentEmail,
      residentProfilePicture: data.residentProfilePicture,
    }),
    method: methodsConvertor.fromDb(data.method),
    details: data.method === EMethodDB.SIMPLE ? null : applicationDetailConverter.fromDb(data.details[data.method]),
  }),
};

export const statusChangeHistoryConverter: TFetchConverter<TStatusChangeHistoryUI, TStatusChangeHistoryDB> = {
  fromDb(data) {
    const details = {
      reason: data.reason?.replace(hudGuidelinesLink, 'document'),
      by: data.by || 'N/A',
      date: formattedDateTimezone(data.date, 'MM/DD/YYYY hh:mm A'),
    };
    if (data.kind === EStatusChangeKind.RESIDENCY) {
      return {
        ...details,
        kind: EStatusChangeKind.RESIDENCY,
        move: data.move,
        from: animalResidencyTagConverter.fromDb(data.from),
        to: animalResidencyTagConverter.fromDb(data.to),
      };
    }
    return {
      ...details,
      kind: EStatusChangeKind.APPLICATION,
      from: animalApplicationStatusConverter.fromDb(data.from),
      to: animalApplicationStatusConverter.fromDb(data.to),
    };
  },
};

export const getApplicantAnimalDetailsConverter: TFetchConverter<TAnimalApplicantDetailUi, TAnimalApplicantDetail> = {
  fromDb: (data) => {
    return {
      trainedFor: data.trainedFor,
      isRequiredBecauseOfDisability: data.isRequiredBecauseOfDisability,
      hasObservableDisability: data.hasObservableDisability,
      animalMoveInDate: formattedDate(data.animalMoveInDate),
      animal: animalBasicInfoConvertor.fromDb(data.animal),
      canRetractRejection: data.canRetractRejection,
      canNotReapplyReason: data.canNotReapplyReason || null,
      hpDocPackets: (data.hpDocPackets || []).map((packet) => ({
        ...getHPDocPacketsConvertor.fromDb(packet),
      })),
      ownerInfo: animalOwnerConvertor.fromDb(data.ownerInfo),
      saDocuments: (data.saDocuments || []).map((item) => applicationSADocPacketConvertor.fromDb(item)),
      residencyStatus: animalResidencyStatusConverter.fromDb(data.residencyStatus),
      splitStatus: animalStatusDetailsConverter.fromDb({
        statusDetails: data.statusDetails,
        residencyStatus: data.residencyStatus,
      }),
      residencyStatusChangeInfo: data.residencyStatusChangeInfo
        ? statusChangeInfoConverter.fromDb(data.residencyStatusChangeInfo)
        : null,
      vetRecords: (data.vetRecords || []).map((item) => animalVetRecordConvertor.fromDb(item)),
      dataOverview: dataOverviewConvertor.fromDb({
        ...data.dataOverview,
        ...(!data.ownerInfo.isSigned && {
          isPolicySigned: EPolicyOverviewStatusDB.MISSING,
        }),
      }),
      statusChangeHistory: data.statusChangeHistory.map((item) => statusChangeHistoryConverter.fromDb(item)).reverse(),
      isToPetConverted: data.isToPetConverted,
      policyContent: data.policyContent ? b64toBlob(data.policyContent, 'text/html') : null,
      discoveredTagReason: data.discoveredTagReason,
      tags: (data.tags || []).map((tag) => animalTagsConverter.fromDb(tag)),
      statusUpdateLocked: data.statusUpdateLocked,
      isMoveInDatePassed: data.isMoveInDatePassed,
      isScreeningEnabled: data.isScreeningEnabled,
      toPetConvertedReason: data.toPetConvertedReason,
      missingNameReason: data.missingNameReason,
      missingTypeReason: data.missingTypeReason,
      missingMoveInDateReason: data.missingMoveInDateReason,
      toPetConvertedDate: data.toPetConvertedDate ? formattedDateTimezone(data.toPetConvertedDate) : null,
      shouldTenantSignAddendum: data.shouldTenantSignAddendum,
      animalRestrictionStatus: animalRestrictionStatusConvertor.fromDb(data.animalRestrictionStatus),
      animalRestrictionTags: (data.animalRestrictionTags || []).map((tag) => animalRestrictionTagConvertor.fromDb(tag)),
      ticketId: data.ticketId,
      paymentStatus: data.paymentStatus ? animalPaymentStatusConverter.fromDb(data.paymentStatus) : null,
      deactivationStatusChangeInfo: data.deactivationStatusChangeInfo
        ? statusChangeInfoConverter.fromDb(data.deactivationStatusChangeInfo)
        : null,
    };
  },
};

export const residencyDetailConverter: TFetchConverter<TApproveApplicationFormProps, TApproveApplicationFormPropsUI> = {
  fromDb: (data) => ({
    propertyId: data.property.value,
    unitNumber: data.unit.label,
    moveInDate: data.moveInDate,
    leaseToDate: data.leaseToDate,
  }),
};

export const approveApplicationConverter: TCreateConverter<TApproveApplicationUI, TApproveApplicationDB> = {
  toDb: ({ residenceId, residencyDetails, conditionalApproveDetails, ...target }) => {
    return {
      ...target,
      ...(residenceId && { residenceId }),
      ...(residencyDetails && {
        residencyDetails: residencyDetailConverter.fromDb(residencyDetails),
      }),
      ...(conditionalApproveDetails && {
        conditionalApproveDetails: {
          dueDate: conditionalApproveDetails.dueToDate,
          reason: conditionalApproveDetails.reason,
        },
      }),
    };
  },
};

export const applicationVerificationMessageConvertor: TFetchConverter<TVerificationMessageUi, TVerificationMessageDB> =
  {
    fromDb: (data) => ({
      message: data.result.length ? data.result[0].reason : undefined,
    }),
  };

export const approvePassedMoveInApplicationConvertor: TFetchConverter<
  TApprovePassedMoveInApplicationUI,
  TApprovePassedMoveInApplicationDB
> = {
  fromDb: (data) => ({
    isResidencyCreated: data.residencyCreated,
    animalResidencyId: data.animalResidencyId,
  }),
};

export const applicationAnimalScreeningConvertor: TCreateConverter<
  TApplicantAnimalScreeningUI,
  TApplicantAnimalScreeningDB
> = {
  toDb: (data) => ({
    applicationId: data.applicationId,
    needToScreen: data.needToScreen,
  }),
};
